import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  PaymentType,
  PaymentsRoutesResponse,
  ToggleSwitch,
  TrustPaymentsCreateSessionSuccess,
  TrustPaymentsFxQuoteFx,
  TrustPaymentsKycFieldKey,
  trustPaymentsService,
  v4Service,
} from "src/api";
import { useUrlParamsContext } from "src/contexts";

const QUOTE_REFRESH_INTERVAL_SEC = 30;

interface RouteMeta {
  source_currency_id?: string;
  target_currency_id?: string;
  // When true, the route opts into local-currency FX. Widget calls
  // POST /psp/trustpayments/quote-deposit instead of the existing
  // conversion-quote + fee-estimate path; UI switches to the
  // local-currency breakdown headline.
  fx_enabled?: boolean;
  // Deposit only: the user enters how much to SPEND in their local
  // currency and the quote derives the crypto received (inverse of the
  // default flow). The wire call is identical — `amount` is interpreted
  // as local fiat by the backend; only the labels + input precision flip.
  reverse?: boolean;
}

// State + actions for the Trust Payments deposit form. The host opens
// the widget bound to a single `paymentRouteId`; we derive source /
// target currencies from the route's `meta.source_currency_id` /
// `meta.target_currency_id` (with `currency_id` as the fallback).
export const useTrustPaymentsDeposit = (route: PaymentsRoutesResponse) => {
  const { paymentRouteId } = useUrlParamsContext();

  const routeMeta = useMemo<RouteMeta>(() => {
    try {
      return route.meta ? (JSON.parse(route.meta) as RouteMeta) : {};
    } catch {
      return {};
    }
  }, [route.meta]);

  const sourceCurrency = routeMeta.source_currency_id ?? route.currency_id ?? "";
  const targetCurrency = routeMeta.target_currency_id ?? route.currency_id ?? "";
  const targetCurrencyPrecision = route.currency?.precision ?? 2;
  const sourceCurrencyPrecision = sourceCurrency === targetCurrency ? targetCurrencyPrecision : 2;
  const fxEnabled = routeMeta.fx_enabled === true;
  // Reverse only applies to FX deposits. In reverse the input is local
  // fiat (2 decimals), not the target stablecoin's precision.
  const reverse = fxEnabled && routeMeta.reverse === true;
  const inputPrecision = reverse ? 2 : targetCurrencyPrecision;

  const [amount, setAmount] = useState<string>("");
  const [amountTouched, setAmountTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [kycFieldsRequired, setKycFieldsRequired] = useState<TrustPaymentsKycFieldKey[]>([]);
  // User types the TARGET stablecoin amount; the conversion quote tells
  // us the SOURCE fiat card charge, while payment fee is estimated via
  // the same `payments_fees` path the backend stores on the payment.
  const [platformFee, setPlatformFee] = useState<number>(0);
  const [feeCurrency, setFeeCurrency] = useState<string>("");
  const [sourceAmount, setSourceAmount] = useState<number>(0);
  const [estimatedReceive, setEstimatedReceive] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState<number>(QUOTE_REFRESH_INTERVAL_SEC);
  // FX-only state. When the route is non-FX, `fxQuote` stays null and
  // the rest of the hook behaves identically to the legacy flow.
  const [fxQuote, setFxQuote] = useState<TrustPaymentsFxQuoteFx | null>(null);
  const [fxError, setFxError] = useState<string | null>(null);
  // Local fiat resolved on mount (no amount) so labels read in local currency before a quote.
  const [localCurrencyHint, setLocalCurrencyHint] = useState<string | null>(null);

  // `amount` is the TARGET stablecoin the user wants credited
  // (USDC/EURC). Trust still authorises the computed SOURCE fiat
  // `sourceAmount`, returned by the dry-run quote.
  const targetAmount = Number(amount);
  const amountValid = Number.isFinite(targetAmount) && targetAmount > 0;
  // In reverse the input is the LOCAL fiat to SPEND, not the target
  // crypto — so it must NEVER fall back into the receive amount. Reverse
  // receive comes solely from the backend quote (`credit_amount` /
  // `total_to_receive`); without a quote there is no client-side estimate.
  const grossReceiveAmount = reverse ? estimatedReceive : estimatedReceive || targetAmount;
  // On FX routes the backend owns the full quote — display its
  // `total_to_receive` verbatim (it already accounts for the real
  // USDC→USD conversion and the single platform fee). The legacy
  // non-FX path keeps the client-side subtraction.
  const totalReceiveAmount = fxQuote
    ? fxQuote.total_to_receive
    : reverse
      ? 0
      : Math.max(grossReceiveAmount - platformFee, 0);
  const platformFeeExceedsReceive = amountValid && !quoteLoading && platformFee > 0 && grossReceiveAmount <= platformFee;

  const fetchQuote = useCallback(async () => {
    setQuoteLoading(true);
    setFxError(null);
    try {
      if (fxEnabled) {
        const response = await trustPaymentsService.quoteDeposit({
          payment_route_id: paymentRouteId,
          amount: targetAmount,
        });
        if (response.fx) {
          setFxQuote(response);
          setPlatformFee(response.fees.platform_fee);
          setFeeCurrency(targetCurrency);
          setSourceAmount(response.total_base_amount);
          setEstimatedReceive(response.credit_amount);
          setPrice(response.fx_rate_effective);
        } else {
          setFxQuote(null);
          setPlatformFee(response.fees.platform_fee);
          setFeeCurrency(targetCurrency);
          setSourceAmount(response.total_base_amount);
          setEstimatedReceive(response.credit_amount);
          setPrice(0);
          if (reverse) {
            // Reverse has no USD-only fallback (the input is local fiat),
            // so any non-FX result must block submit, not silently degrade.
            // Map each reason to a distinct, actionable widget error.
            const reasonToError: Record<string, string> = {
              amount_below_min: "FX_AMOUNT_BELOW_MIN",
              amount_above_max: "FX_AMOUNT_ABOVE_MAX",
              country_not_mapped: "FX_COUNTRY_NOT_MAPPED",
            };
            setFxError(reasonToError[response.reason] ?? "FX_PROVIDER_UNAVAILABLE");
          } else if (response.reason === "fx_provider_unavailable") {
            setFxError("FX_PROVIDER_UNAVAILABLE");
          }
        }
        setSecondsUntilRefresh(QUOTE_REFRESH_INTERVAL_SEC);
      } else {
        const [quote, paymentFee] = await Promise.all([
          v4Service.createConversionQuote({
            source_currency_id: sourceCurrency,
            target_currency_id: targetCurrency,
            target_currency_amount: targetAmount,
            fees_in_price: true,
            dry_run: ToggleSwitch.On,
            ttl: 30,
          }),
          v4Service.estimatePaymentFee({
            payment_route_id: paymentRouteId,
            type: PaymentType.Deposit,
            amount: targetAmount,
          }),
        ]);
        setPlatformFee(Number(paymentFee) || 0);
        setFeeCurrency(targetCurrency);
        setSourceAmount(Number(quote.source_currency_amount) || 0);
        setEstimatedReceive(Number(quote.target_currency_amount) || 0);
        setPrice(Number(quote.price) || 0);
        setSecondsUntilRefresh(QUOTE_REFRESH_INTERVAL_SEC);
      }
    } catch {
      setPlatformFee(0);
      setFeeCurrency("");
      setSourceAmount(0);
      setEstimatedReceive(0);
      setPrice(0);
      setFxQuote(null);
      if (fxEnabled) setFxError("FX_QUOTE_FAILED");
    } finally {
      setQuoteLoading(false);
    }
  }, [paymentRouteId, sourceCurrency, targetCurrency, targetAmount, fxEnabled, reverse]);

  const quoteDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!paymentRouteId || !sourceCurrency || !targetCurrency || !amountValid) {
      setPlatformFee(0);
      setFeeCurrency("");
      setSourceAmount(0);
      setEstimatedReceive(0);
      setPrice(0);
      setQuoteLoading(false);
      setSecondsUntilRefresh(QUOTE_REFRESH_INTERVAL_SEC);
      setFxQuote(null);
      setFxError(null);
      return;
    }
    setQuoteLoading(true);
    quoteDebounceRef.current = setTimeout(fetchQuote, 300);
    return () => {
      if (quoteDebounceRef.current) clearTimeout(quoteDebounceRef.current);
    };
  }, [paymentRouteId, sourceCurrency, targetCurrency, amountValid, fetchQuote]);

  // Pauses once sessionId is set — quote is locked into the session.
  useEffect(() => {
    if (!paymentRouteId || !amountValid || sessionId !== null) return;
    const interval = setInterval(() => {
      setSecondsUntilRefresh((prev) => {
        if (prev <= 1) {
          fetchQuote();
          return QUOTE_REFRESH_INTERVAL_SEC;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [paymentRouteId, amountValid, sessionId, fetchQuote]);

  // Best-effort local-currency resolve on mount for FX routes; falls back to generic copy on failure.
  useEffect(() => {
    if (!fxEnabled || !paymentRouteId) return;
    let active = true;
    trustPaymentsService
      .getLocalCurrency()
      .then((res) => {
        if (active) setLocalCurrencyHint(res.local_currency);
      })
      .catch(() => {
        if (active) setLocalCurrencyHint(null);
      });
    return () => {
      active = false;
    };
  }, [fxEnabled, paymentRouteId]);

  // Live quote currency wins; the mount-time hint covers the pre-amount state.
  const localCurrency = fxQuote?.local_currency ?? localCurrencyHint;

  // On FX routes we only block Pay when the FX provider is actually
  // down (`fxError`). Structural `fx: false` (country_not_mapped,
  // same_currency, currency_unsupported_by_psp) silently falls back
  // to the route's base currency — submit goes through without a
  // quote_id and BE charges in USD/EUR like the legacy flow.
  const submitDisabled =
    !paymentRouteId ||
    !amountValid ||
    quoteLoading ||
    sourceAmount <= 0 ||
    platformFeeExceedsReceive ||
    loading ||
    sessionId !== null ||
    (fxEnabled && !!fxError);

  const handleAmountChange = useCallback(
    (value: string) => {
      const [integerPart = "", ...decimalParts] = value.replace(/[^0-9.]/g, "").split(".");
      const decimalPart = decimalParts.join("").slice(0, inputPrecision);
      const sanitized = decimalParts.length ? `${integerPart}.${decimalPart}` : integerPart;
      setAmount(sanitized);
      setError(null);
    },
    [inputPrecision]
  );

  const submit = useCallback(async () => {
    if (!paymentRouteId || !amountValid || platformFeeExceedsReceive) return;
    if (fxEnabled && !!fxError) return;
    setLoading(true);
    setError(null);
    try {
      // Trust JWT `baseamount` is the FIAT charge returned by the
      // quote for the user's desired stablecoin receive amount.
      const response = await trustPaymentsService.createSession({
        data: {
          payment_route_id: paymentRouteId,
          amount: sourceAmount,
          type: "deposit",
          ...(fxQuote ? { quote_id: fxQuote.quote_id } : {}),
        },
      });

      if (!response.success) {
        setError(response.error || "TRUSTPAYMENTS_SESSION_FAILED");
        return;
      }

      const ok = response as TrustPaymentsCreateSessionSuccess;
      setSessionId(ok.session_id);
      setKycFieldsRequired(ok.kyc_fields_required ?? []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [paymentRouteId, amountValid, platformFeeExceedsReceive, sourceAmount, fxEnabled, fxQuote, fxError]);

  const reset = useCallback(() => {
    setSessionId(null);
    setKycFieldsRequired([]);
    setAmount("");
    setAmountTouched(false);
    setError(null);
    setFxQuote(null);
    setFxError(null);
  }, []);

  return {
    sourceCurrency,
    targetCurrency,
    sourceCurrencyPrecision,
    targetCurrencyPrecision,
    amount,
    handleAmountChange,
    amountTouched,
    setAmountTouched,
    amountValid,
    targetAmount,
    sourceAmount,
    loading,
    error,
    sessionId,
    kycFieldsRequired,
    submit,
    submitDisabled,
    reset,
    platformFee,
    feeCurrency,
    quoteLoading,
    estimatedReceive,
    totalReceiveAmount,
    platformFeeExceedsReceive,
    price,
    secondsUntilRefresh,
    fxEnabled,
    reverse,
    fxQuote,
    fxError,
    localCurrency,
  };
};
